import * as React from "react"
import { Input } from "./input"
import { cn } from "../../lib/utils"

const InputOTP = React.forwardRef(({ 
  value: valueProp, 
  defaultValue = "", 
  onChange, 
  maxLength = 6,
  onComplete,
  error = false,
  success = false,
  ...props 
}, ref) => {
  const [value, setValue] = React.useState(defaultValue)
  const inputRefs = React.useRef([])

  // Handle prop value changes
  React.useEffect(() => {
    if (valueProp !== undefined) {
      setValue(valueProp)
    }
  }, [valueProp])

  const handleChange = (newValue) => {
    const finalValue = newValue.slice(0, maxLength)
    setValue(finalValue)
    
    if (onChange) {
      onChange(finalValue)
    }
    
    // Call onComplete when we have all digits
    if (finalValue.length === maxLength && onComplete) {
      onComplete(finalValue)
    }
  }

  const handleInput = (index, inputValue) => {
    if (!inputValue.match(/^\d*$/)) return // Only allow digits
    
    const newValue = value.split('')
    newValue[index] = inputValue
    handleChange(newValue.join(''))
    
    // Auto-focus next input if we have a digit
    if (inputValue && index < maxLength - 1) {
      setTimeout(() => {
        if (inputRefs.current[index + 1]) {
          inputRefs.current[index + 1].focus()
        }
      }, 0)
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      setTimeout(() => {
        if (inputRefs.current[index - 1]) {
          inputRefs.current[index - 1].focus()
          inputRefs.current[index - 1].select()
        }
      }, 0)
    } else if (e.key === 'ArrowLeft' && index > 0) {
      // Move to previous input
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus()
      }
    } else if (e.key === 'ArrowRight' && index < maxLength - 1) {
      // Move to next input
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus()
      }
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, maxLength)
    
    if (pasteData) {
      handleChange(pasteData)
      
      // Focus the last filled input or the first empty one
      const focusIndex = Math.min(pasteData.length, maxLength - 1)
      setTimeout(() => {
        if (inputRefs.current[focusIndex]) {
          inputRefs.current[focusIndex].focus()
        }
      }, 0)
    }
  }

  // Determine border color based on state
  const getBorderColor = () => {
    if (error) return "border-red-500 ring-red-500 ring-2"
    if (success) return "border-green-500 ring-green-500 ring-2"
    return "border-input"
  }

  return (
    <div 
      ref={ref} 
      {...props} 
      className={cn("flex items-center gap-2", props.className)}
    >
      {Array.from({ length: maxLength }).map((_, index) => (
        <Input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={value[index] || ""}
          onChange={(e) => handleInput(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={cn(
            "w-12 h-12 text-center text-2xl font-medium",
            "border-y border-r first:rounded-l-md first:border-l last:rounded-r-md",
            getBorderColor()
          )}
        />
      ))}
    </div>
  )
})
InputOTP.displayName = "InputOTP"

export { InputOTP }